import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LinkRouter from "components/navigation/LinkRouter";
import useIsMobile from "components/hooks/useIsMobile";
import {
  ChevronDown,
  ChevronUp,
  Edit,
  ExternalLink,
  Ghost,
  MailCheck,
  MessageSquare,
  Clipboard,
  Landmark,
} from "lucide-react";

import {
  commaSeparatedEnglishList,
  formatUSPhoneNumber,
} from "../../common/utils";
import { Advisor, AdvisorStatus } from "protogen/common_pb";
import { FamilyStatus } from "protogen/advisors_service_pb";
import { AdvisorAdministrationFamily } from "protogen/admin_service_pb";
import { getFormattedLocation } from "components/family/utils";

import { UserAvatar } from "components/common/CurrentUserAvatar";
import Collapse from "@mui/material/Collapse";

import MetadataSection from "../../components/common/MetadataSection";
import FreeAdvisorIdentityAction from "./FreeAdvisorIdentityAction";
import { UpdateAdvisorRequest } from "../../protogen/advisors_service_pb";
import { PROD_MATCHMAKING_FORM_UUID } from "components/documents/constants";
import { useUpdateAdvisor } from "../../services/advisor";
import {
  useAdminUpdateFeatureFlag,
  useAdminUpdateAdvisorService,
} from "../../services/admin";
import Toggle from "components/common/Toggle";
import { PartialMessage } from "@bufbuild/protobuf";
import AdvisorAdminNotes from "./AdvisorAdminNotes";
import { FEATURE_FLAGS, FeatureFlag } from "components/helpers/GatedComponent";
import {
  AdvisorServicesSection,
  ServiceScope,
} from "components/advisor/AdvisorServicesSettings";
import Divider from "@mui/material/Divider";
import AdvisorReviewsComponent from "components/review/AdvisorReviews";
import { useCreateMercuryRecipientInvite } from "services/billing";

const AdvisorFeatureFlag = ({
  flag,
  advisor,
  onFlagUpdate,
}: {
  flag: FeatureFlag;
  advisor: Advisor;
  onFlagUpdate: (flagName: string, enabled: boolean) => void;
}) => {
  const isEnabled = advisor.enabledFeatureFlags.includes(flag.value);
  const { request, loading } = useAdminUpdateFeatureFlag();

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    await request({
      userRef: advisor.ref,
      flagName: flag.value,
      enabled: newValue,
    });
    onFlagUpdate(flag.value, newValue);
  };

  return (
    <ListItem sx={{ padding: "4px 0" }}>
      <ListItemText
        primary={flag.title}
        secondary={flag.description}
        primaryTypographyProps={{ variant: "bodySmall" }}
        secondaryTypographyProps={{ variant: "bodySmall" }}
      />
      <Switch
        edge="end"
        size="small"
        onChange={onChange}
        checked={isEnabled}
        disabled={loading}
      />
    </ListItem>
  );
};

const AdvisorFeatureFlags = ({
  advisor,
  onFlagUpdate,
}: {
  advisor: Advisor;
  onFlagUpdate: (flagName: string, enabled: boolean) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const flags = Object.values(FEATURE_FLAGS);

  return (
    <Box sx={{ marginTop: "12px" }}>
      <Box
        display="flex"
        alignItems="center"
        sx={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="bodyHeavy" sx={{ marginRight: "4px" }}>
          Feature Flags
        </Typography>
        <IconButton size="small">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List dense sx={{ padding: 0 }}>
          {flags.map((flag) => (
            <AdvisorFeatureFlag
              key={flag.value}
              flag={flag}
              advisor={advisor}
              onFlagUpdate={onFlagUpdate}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

const AdvisorMetadata = ({ advisor }: { advisor: Advisor }) => {
  const { request } = useUpdateAdvisor();
  if (advisor.metadata === undefined) return null;
  return (
    <Box
      sx={{
        marginTop: "24px",
        marginBottom: "12px",
      }}
    >
      <MetadataSection
        metadata={advisor.metadata}
        saveMetadata={async (metadata) => {
          await request(
            new UpdateAdvisorRequest({
              advisorRef: advisor.ref,
              shouldUpdateMetadata: true,
              updatedMetadata: metadata,
              shouldUpdateSpecialties: false,
            }),
          );
        }}
        entityType={"advisor"}
      />
    </Box>
  );
};

const AdvisorReviews = ({ advisor }: { advisor: Advisor }) => {
  const [showReviews, setShowReviews] = useState(false);

  return (
    <Box>
      <Divider sx={{ marginBottom: "16px" }} />
      <Box
        sx={{
          padding: "0 32px 24px 32px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="bodyHeavy">Reviews</Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => setShowReviews(!showReviews)}
          >
            {showReviews ? "Hide" : "Show Reviews"}
          </Button>
        </Box>
        {showReviews && (
          <AdvisorReviewsComponent advisor={advisor} isAdmin={true} />
        )}
      </Box>
    </Box>
  );
};

const AdvisorSettings = ({ advisor }: { advisor: Advisor }) => {
  const [_advisor, setAdvisor] = useState(advisor);
  const { request, loading } = useUpdateAdvisor();
  const update = async (change: PartialMessage<UpdateAdvisorRequest>) => {
    if (loading) return;
    const resp = await request(
      new UpdateAdvisorRequest({
        advisorRef: _advisor.ref,
        shouldUpdateSpecialties: false,
        ...change,
      }),
    );
    if (resp?.advisor) {
      setAdvisor(resp.advisor);
    }
  };
  useEffect(() => {
    setAdvisor(advisor);
  }, [advisor]);
  return (
    <Box sx={{ marginTop: "12px" }}>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "6px" }}>
        Settings
      </Typography>
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateEmailNotifications: true,
            emailNotificationsEnabled: e.target.checked,
          })
        }
        checked={_advisor.emailNotificationsEnabled}
        label="Email notifications"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateNewClientOpportunitiesEnabled: true,
            newClientOpportunitiesEnabled: e.target.checked,
          })
        }
        checked={_advisor.newClientOpportunitiesEnabled}
        label="New opportunities"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateHasCompletedOnboardingSession: true,
            hasCompletedOnboardingSession: e.target.checked,
          })
        }
        checked={_advisor.hasCompletedOnboardingSession}
        label="Onboarding completed"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateHasCompletedMatchmakingQuestionnaire: true,
            hasCompletedMatchmakingQuestionnaire: e.target.checked,
          })
        }
        checked={_advisor.hasCompletedMatchmakingQuestionnaire}
        label="Matchmaking questionnaire"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateHasSetupApp: true,
            hasSetupApp: e.target.checked,
          })
        }
        checked={_advisor.hasSetupApp}
        label="App installed"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateHasCompletedOutreachScorecard: true,
            hasCompletedOutreachScorecard: e.target.checked,
          })
        }
        checked={_advisor.hasCompletedOutreachScorecard}
        label="Outreach Scorecard"
      />
      <Toggle
        sx={{ ".MuiSwitch-root": { margin: "5px 0px" } }}
        onChange={(e) =>
          update({
            shouldUpdateSmsBotEnabled: true,
            smsBotEnabled: e.target.checked,
          })
        }
        checked={_advisor.smsBotEnabled}
        label="FayeBot SMS"
      />
    </Box>
  );
};

const getStatus = (status: AdvisorStatus) => {
  switch (status) {
    case AdvisorStatus.ACTIVE:
      return <Chip variant="outlined" color="success" label={"Active"} />;
    case AdvisorStatus.PENDING:
      return <Chip variant="outlined" color="warning" label={"Pending"} />;
    case AdvisorStatus.DEACTIVATED:
      return <Chip variant="outlined" color="error" label={"Deactivated"} />;
    case AdvisorStatus.STAFF:
      return <Chip variant="outlined" label={"Staff"} />;
    case AdvisorStatus.TEST:
      return <Chip variant="outlined" label={"Test"} />;
    default:
      return <Chip variant="outlined" label={"?"} />;
  }
};

const FamilyStatusSection = ({
  label,
  families,
  loading,
  color = "#198282",
}: {
  label: string;
  families: AdvisorAdministrationFamily[];
  loading: boolean;
  color?: string;
}) => (
  <Box>
    <Typography variant="bodySmallHeavy" color="#262626">
      {label}
    </Typography>
    <Typography variant="body">
      {loading ? (
        <i>Loading...</i>
      ) : families.length === 0 ? (
        <i>None</i>
      ) : (
        families.map((f: AdvisorAdministrationFamily, idx: number) => (
          <Fragment key={f.ref}>
            <LinkRouter
              inline
              to={`/families/${encodeURIComponent(f.ref)}#tab-admin`}
              sx={{ color }}
            >
              {f.name}
              {f.isPaused && (
                <Tooltip title="On Service Pause">
                  <span>*</span>
                </Tooltip>
              )}
            </LinkRouter>
            {idx < families.length - 1 ? ", " : ""}
          </Fragment>
        ))
      )}
    </Typography>
    {!loading && families.some((f) => f.isPaused) && (
      <Typography variant="bodySmall" color="text.secondary" component="div">
        * On Service Pause
      </Typography>
    )}
  </Box>
);

type AdvisorCardProps = {
  advisor: Advisor;
  allFamilies: AdvisorAdministrationFamily[];
  familiesLoading: boolean;
  forceOpen: boolean;
  onEdit: () => void;
  onClose: () => void;
  onOpen: () => void;
};

export default ({
  advisor: initialAdvisor,
  allFamilies,
  familiesLoading,
  forceOpen,
  onEdit,
  onClose,
  onOpen,
}: AdvisorCardProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [advisor, setAdvisor] = useState(initialAdvisor);

  const activeFamilies = allFamilies.filter(
    (f) => f.status === FamilyStatus.ACTIVE,
  );
  const deactivatedFamilies = allFamilies.filter(
    (f) =>
      f.status === FamilyStatus.DEACTIVATED ||
      f.status === FamilyStatus.DIGITAL_DEACTIVATED ||
      f.status === FamilyStatus.DIGITAL_DEACTIVATED_TRIAL,
  );
  const deactivatedProspectFamilies = allFamilies.filter(
    (f) => f.status === FamilyStatus.DEACTIVATED_PROSPECT,
  );
  const prospectFamilies = allFamilies.filter(
    (f) => f.status === FamilyStatus.PROSPECT,
  );
  const preactivationFamilies = allFamilies.filter(
    (f) => f.status === FamilyStatus.PREACTIVATION,
  );
  const testFamilies = allFamilies.filter(
    (f) =>
      f.status === FamilyStatus.TEST || f.status === FamilyStatus.TEST_HIDDEN,
  );
  const demoFamilies = allFamilies.filter(
    (f) => f.status === FamilyStatus.DEMO,
  );
  const unpaidSignupFamilies = allFamilies.filter(
    (f) => f.status === FamilyStatus.UNPAID_SIGNUP,
  );
  const notSetFamilies = allFamilies.filter(
    (f) => f.status === FamilyStatus.NOT_SET,
  );

  const byline =
    commaSeparatedEnglishList(advisor.specialties) || advisor.blurbText;
  const location = `${advisor.city}, ${advisor.state}`;
  const baseUrl = window.location.origin;
  const matchmakingFormUrl = `${baseUrl}/forms/complete/${PROD_MATCHMAKING_FORM_UUID}`;
  const [copied, setCopied] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [showAllFamilies, setShowAllFamilies] = useState(false);
  const [mercuryInviteSent, setMercuryInviteSent] = useState(false);
  const { request: updateServiceRequest, loading: serviceLoading } =
    useAdminUpdateAdvisorService();
  const {
    request: createMercuryRecipientInvite,
    loading: mercuryInviteLoading,
    error: mercuryInviteError,
  } = useCreateMercuryRecipientInvite();

  useEffect(() => {
    setAdvisor(initialAdvisor);
  }, [initialAdvisor]);

  const handleFlagUpdate = (flagName: string, enabled: boolean) => {
    setAdvisor((prev) => {
      const newFlags = enabled
        ? [...prev.enabledFeatureFlags, flagName]
        : prev.enabledFeatureFlags.filter((f) => f !== flagName);
      return new Advisor({ ...prev, enabledFeatureFlags: newFlags });
    });
  };

  const handleServiceChange = async (
    serviceName: string,
    enabled: boolean,
    scope: ServiceScope,
  ) => {
    await updateServiceRequest({
      advisorRef: advisor.ref,
      serviceName,
      enabled,
      scope,
    });
    setAdvisor((prev) => {
      const newServices = enabled
        ? [...prev.enabledServices, serviceName]
        : prev.enabledServices.filter((s) => s !== serviceName);
      const newScopes = { ...prev.serviceScopes };
      if (enabled && scope) {
        newScopes[serviceName] = scope;
      } else {
        delete newScopes[serviceName];
      }
      return new Advisor({
        ...prev,
        enabledServices: newServices,
        serviceScopes: newScopes,
      });
    });
  };

  const toggleOpen = () => {
    if (open) {
      onClose();
    } else {
      onOpen();
    }
    setOpen(!open);
  };
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="8px"
      sx={{
        borderRadius: "8px",
        border: "1px solid #ECECEC",
        background: "#FFF",
        width: "100%",
        padding: "16px 24px",
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap="12px"
        sx={{ cursor: !open ? "pointer" : undefined }}
        onClick={() => !open && toggleOpen()}
      >
        <Box flex={0} alignSelf="flex-start">
          <LinkRouter
            to={`/advisor/${encodeURIComponent(advisor.ref)}`}
            useSpan={true}
          >
            <UserAvatar
              user={advisor}
              size={48}
              clickable={false}
              advisorInternalLink={true}
            />
          </LinkRouter>
        </Box>
        <Box flex={5}>
          <Box display="flex" flexDirection={"column"} gap="4px">
            <LinkRouter
              to={`/advisor/${encodeURIComponent(advisor.ref)}`}
              useSpan={true}
            >
              <Typography
                variant="bodyHeavy"
                color="#262626"
              >{`${advisor.firstName} ${advisor.lastName}`}</Typography>
            </LinkRouter>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
              <Typography
                variant="body"
                color="text.secondary"
                sx={{
                  whiteSpace: bioExpanded ? "pre-wrap" : "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: bioExpanded ? "none" : "350px",
                }}
              >
                {isMobile ? location : byline}
              </Typography>
              {!isMobile && !bioExpanded && byline && byline.length > 40 && (
                <Typography
                  component="span"
                  variant="body"
                  sx={{ color: "#198282", cursor: "pointer", flexShrink: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBioExpanded(true);
                  }}
                >
                  more
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box flex={2} justifyContent="end" display={isMobile ? "none" : "flex"}>
          <Typography
            variant="body"
            color="#262626"
            sx={{
              textAlign: "right",
            }}
          >
            {location}
          </Typography>
        </Box>
        <Box flex={2} display={isMobile ? "none" : "flex"} justifyContent="end">
          {getStatus(advisor.status)}
        </Box>
        <Box flex={0}>
          <IconButton aria-label="expand row" size="small" onClick={toggleOpen}>
            {open ? <ChevronUp /> : <ChevronDown />}
          </IconButton>
        </Box>
      </Box>
      {isMobile && byline && (
        <Typography variant="bodySmall" color="#6B6E7B">
          {byline}
        </Typography>
      )}
      <Collapse in={open || forceOpen} timeout="auto" unmountOnExit>
        <div
          style={{
            marginTop: "12px",
            height: "1px",
            background: "#E2E3E4",
          }}
        />
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
            <Box sx={{ padding: "24px 32px" }} flex={2}>
              <Typography variant="h3" marginBottom="20px">
                Details
              </Typography>
              <Box display="flex" flexDirection="column" gap="10px">
                <Box>
                  <Typography variant="bodyHeavy">Faye email</Typography>
                  <Typography variant="body">{advisor.username}</Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Faye number</Typography>
                  <Typography variant="body">
                    {formatUSPhoneNumber(advisor.advisorPhone)}
                  </Typography>
                </Box>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="bodyHeavy">Families</Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowAllFamilies(!showAllFamilies)}
                      sx={{ minWidth: "unset", padding: "4px 8px" }}
                    >
                      {showAllFamilies ? "Show Less" : "Show All"}
                    </Button>
                  </Box>
                  <Box display="flex" flexDirection="column" gap="10px">
                    <FamilyStatusSection
                      label="Active"
                      families={activeFamilies}
                      loading={familiesLoading}
                    />
                    <FamilyStatusSection
                      label="Deactivated"
                      families={deactivatedFamilies}
                      loading={familiesLoading}
                      color="#9E9E9E"
                    />
                    {showAllFamilies && (
                      <>
                        {deactivatedProspectFamilies.length > 0 && (
                          <FamilyStatusSection
                            label="Deactivated Prospect"
                            families={deactivatedProspectFamilies}
                            loading={familiesLoading}
                            color="#9E9E9E"
                          />
                        )}
                        {prospectFamilies.length > 0 && (
                          <FamilyStatusSection
                            label="Prospect"
                            families={prospectFamilies}
                            loading={familiesLoading}
                          />
                        )}
                        {preactivationFamilies.length > 0 && (
                          <FamilyStatusSection
                            label="Preactivation"
                            families={preactivationFamilies}
                            loading={familiesLoading}
                          />
                        )}
                        {testFamilies.length > 0 && (
                          <FamilyStatusSection
                            label="Test"
                            families={testFamilies}
                            loading={familiesLoading}
                          />
                        )}
                        {demoFamilies.length > 0 && (
                          <FamilyStatusSection
                            label="Demo"
                            families={demoFamilies}
                            loading={familiesLoading}
                          />
                        )}
                        {unpaidSignupFamilies.length > 0 && (
                          <FamilyStatusSection
                            label="Unpaid Signup"
                            families={unpaidSignupFamilies}
                            loading={familiesLoading}
                          />
                        )}
                        {notSetFamilies.length > 0 && (
                          <FamilyStatusSection
                            label="Not Set"
                            families={notSetFamilies}
                            loading={familiesLoading}
                          />
                        )}
                      </>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Personal email</Typography>
                  <Typography variant="body">
                    {advisor.personalEmail}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Advisor since</Typography>
                  <Typography variant="body">
                    {new Date(
                      Number(advisor.createdAtSec) * 1000,
                    ).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Personal phone</Typography>
                  <Typography variant="body">
                    {formatUSPhoneNumber(advisor.personalPhone)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Home address</Typography>
                  <Typography variant="body">
                    {getFormattedLocation(advisor.address)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="bodyHeavy">Roles</Typography>
                  <Typography variant="body">
                    {advisor.roles.length === 0 && <i>None</i>}
                    {advisor.roles.map((r) => (
                      <Chip size="small" key={r} variant="outlined" label={r} />
                    ))}
                  </Typography>
                </Box>
              </Box>
              <AdvisorMetadata advisor={advisor} />
            </Box>
            <Box sx={{ padding: "24px 32px" }} flex={2}>
              <Typography variant="bodyHeavy" marginBottom="20px">
                Actions
              </Typography>
              <Box display="flex" flexDirection="column">
                <Button
                  startIcon={<Edit />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={onEdit}
                >
                  Edit advisor
                </Button>
                {mercuryInviteError && (
                  <Typography variant="bodySmall" color="error">
                    {mercuryInviteError}
                  </Typography>
                )}
                <Button
                  startIcon={<MessageSquare />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigate(`/inbox/messages?recipientRefs=${advisor.ref}`);
                  }}
                >
                  Direct Message
                </Button>
                <Button
                  startIcon={<ExternalLink />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    window.open(advisor.profilePath, "_blank");
                  }}
                  disabled={!advisor.isProfilePublic}
                >
                  View public profile
                </Button>
                <Button
                  startIcon={<Ghost />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigate(
                      `/tools/impersonate-user?username=${encodeURIComponent(
                        advisor.username,
                      )}`,
                    );
                  }}
                >
                  Impersonate
                </Button>
                <Button
                  startIcon={<MailCheck />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigate(
                      `/tools/manage-roles?username=${encodeURIComponent(
                        advisor.username,
                      )}`,
                    );
                  }}
                >
                  Manage roles
                </Button>
                <Button
                  startIcon={<Clipboard />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  onClick={() => {
                    navigator.clipboard.writeText(matchmakingFormUrl);
                    setCopied(true);
                    setTimeout(() => {
                      setCopied(false);
                    }, 2000);
                  }}
                >
                  {copied ? "Copied!" : "Copy matching form url to clipboard"}
                </Button>
                <Button
                  startIcon={<Landmark />}
                  variant={"text"}
                  sx={{ justifyContent: "start" }}
                  disabled={
                    advisor.hasSetUpAutomaticPayments ||
                    mercuryInviteLoading ||
                    mercuryInviteSent
                  }
                  onClick={async () => {
                    const response = await createMercuryRecipientInvite({
                      advisorRef: advisor.ref,
                    });
                    if (response?.invited) {
                      setMercuryInviteSent(true);
                    }
                  }}
                >
                  {advisor.hasSetUpAutomaticPayments
                    ? "Mercury configured"
                    : mercuryInviteSent
                      ? "Mercury invite sent"
                      : "Invite to Mercury"}
                </Button>
                <FreeAdvisorIdentityAction advisor={advisor} />
                <AdvisorSettings advisor={advisor} />
                <AdvisorFeatureFlags
                  advisor={advisor}
                  onFlagUpdate={handleFlagUpdate}
                />
                <AdvisorServicesSection
                  advisor={advisor}
                  loading={serviceLoading}
                  onServiceChange={handleServiceChange}
                />
              </Box>
            </Box>
          </Box>
          <AdvisorReviews advisor={advisor} />
          {(forceOpen || open) && <AdvisorAdminNotes advisor={advisor} />}
        </Box>
      </Collapse>
    </Box>
  );
};
